import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { Component, computed, Inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SharedContactRole } from '@app/auth/models/users.model';
import { ContactChipsComponent } from '@app/chat/components/contact-chips/contact-chips.component';
import { Contact, ContactGroupRequest, ContactType, CustomContactGroup } from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { NameCoinComponent } from '@app/shared/components/name-coin/name-coin.component';
import { ButtonType } from '@app/shared/models/dialog-data';
import { messageBox } from '@app/shared/utils/message-box.util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, debounceTime, map, Observable, Subject } from 'rxjs';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    AsyncPipe,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport,
    ContactChipsComponent,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    NameCoinComponent,
    TranslateModule,
    UpperCasePipe,
  ],
  selector: 'app-create-contact-group',
  templateUrl: './create-contact-group.component.html',
  styleUrls: ['./create-contact-group.component.scss'],
})
export class CreateContactGroupComponent implements OnInit {
  protected groupName = '';
  protected groupDescription = '';
  protected search = '';

  protected isShared = false;
  protected allContacts: Contact[] = [];
  protected isSaving = false;

  protected minRoleManage: SharedContactRole = SharedContactRole.OfficeManager;
  protected minRoleDelete: SharedContactRole = SharedContactRole.OfficeManager;
  protected readonly sharedContactRoles = Object.values(SharedContactRole);

  protected managers: Contact[] = [];
  protected deleters: Contact[] = [];

  // Ids from data.managers/deleters that don't resolve to a known contact (e.g. deleted account,
  // outside this viewer's contact scope) are preserved here so saving doesn't silently strip that
  // person's manage/delete rights — see resolveContactsByIds().
  private unresolvedManagerIds: string[] = [];
  private unresolvedDeleterIds: string[] = [];

  protected readonly filteredContacts = signal<Contact[]>([]);
  protected readonly selectedIds = signal<Set<string>>(new Set());
  protected readonly isAllVisibleSelected = computed(() => {
    const contacts = this.filteredContacts();
    const ids = this.selectedIds();
    return contacts.length > 0 && contacts.every((contact) => ids.has(contact.id));
  });

  protected readonly canShowSharedSection$: Observable<boolean>;
  protected readonly canDelete$: Observable<boolean>;

  private readonly searchChanged = new Subject<string>();

  constructor(
    public dialogRef: MatDialogRef<CreateContactGroupComponent, CustomContactGroup>,
    private contactService: ContactService,
    private configService: AppConfigService,
    private snackBar: SnackbarService,
    private appTranslateService: AppTranslateService,
    private dialog: MatDialog,
    private storage: LocalStorageService,
    @Inject(MAT_DIALOG_DATA) protected data: CustomContactGroup | null
  ) {
    // Re-derived from loadedProvision$ and currentUser$ (rather than read once) so a dialog opened
    // before either finishes loading corrects itself once both resolve — canManageCustomContactGroup/
    // canDeleteCustomContactGroup check hasRoleAtLeast() (needs provision) and the group's
    // ownerId/managers/deleters against currentUser.id (needs currentUser), and currentUser starts
    // undefined until ContactService loads it from IDB.
    this.canDelete$ = combineLatest([this.configService.loadedProvision$, this.contactService.currentUser$]).pipe(
      map(() => !!this.data && this.contactService.canDeleteCustomContactGroup(this.data))
    );
    // Creating a new group, or editing a personal one: gated by the domain-wide shared-contacts
    // permission, same as before. Editing an already-shared group: gated by that group's own
    // minRoleManage/managers instead, same algorithm as the edit-icon visibility in
    // ContactListHeaderComponent.
    this.canShowSharedSection$ =
      this.data?.type === 'shared'
        ? combineLatest([this.configService.loadedProvision$, this.contactService.currentUser$]).pipe(
            map(() => this.contactService.canManageCustomContactGroup(this.data!))
          )
        : this.configService.canAddEditSharedContacts$;
  }

  ngOnInit(): void {
    if (this.data) {
      this.groupName = this.data.name;
      this.groupDescription = this.data.description ?? '';
      this.isShared = this.data.type === 'shared';
      this.minRoleManage = this.data.minRoleManage ?? SharedContactRole.OfficeManager;
      this.minRoleDelete = this.data.minRoleDelete ?? SharedContactRole.OfficeManager;
      this.managers = this.resolveContactsByIds(this.data.managers, this.unresolvedManagerIds);
      this.deleters = this.resolveContactsByIds(this.data.deleters, this.unresolvedDeleterIds);
      this.selectedIds.set(new Set(this.data.contacts));
    }

    this.contactService.data$.pipe(untilDestroyed(this)).subscribe((contacts) => {
      this.allContacts = contacts.filter((contact) => contact.directoryListing);
      this.filterContacts();
    });

    this.searchChanged.pipe(debounceTime(150), untilDestroyed(this)).subscribe(() => this.filterContacts());
  }

  protected handleSearchChange(): void {
    this.searchChanged.next(this.search);
  }

  protected trackByContactId(_index: number, contact: Contact): string {
    return contact.id;
  }

  protected contactSubtext(contact: Contact): string {
    return [contact.title, contact.company].filter((value) => !!value).join(' · ');
  }

  protected isSelected(contact: Contact): boolean {
    return this.selectedIds().has(contact.id);
  }

  protected toggleContact(contact: Contact): void {
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(contact.id)) {
        next.delete(contact.id);
      } else {
        next.add(contact.id);
      }
      return next;
    });
  }

  protected toggleSelectAllVisible(): void {
    const shouldSelectAll = !this.isAllVisibleSelected();
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      for (const contact of this.filteredContacts()) {
        if (shouldSelectAll) {
          next.add(contact.id);
        } else {
          next.delete(contact.id);
        }
      }
      return next;
    });
  }

  protected onSharedToggled(): void {
    this.filterContacts();
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      for (const contact of this.allContacts) {
        if (next.has(contact.id) && !this.isContactAllowedForGroupType(contact)) {
          next.delete(contact.id);
        }
      }
      return next;
    });
  }

  protected async handleCreate(): Promise<void> {
    if (!this.groupName.trim() || this.isSaving || this.selectedIds().size === 0) {
      return;
    }

    this.isSaving = true;
    try {
      const request: ContactGroupRequest = {
        name: this.groupName.trim(),
        description: this.groupDescription.trim() || undefined,
        contacts: Array.from(this.selectedIds()),
        type: this.isShared ? 'shared' : 'personal',
        minRoleManage: this.isShared ? this.minRoleManage : undefined,
        minRoleDelete: this.isShared ? this.minRoleDelete : undefined,
        // Deduped: on a first login the group list can arrive before contacts land in
        // contactsById, leaving an id in unresolvedManagerIds/unresolvedDeleterIds that then gets
        // re-added from the picker once contacts load, once resolved into `managers`/`deleters`.
        managers: this.isShared
          ? Array.from(new Set([...this.managers.map((contact) => contact.id), ...this.unresolvedManagerIds]))
          : undefined,
        deleters: this.isShared
          ? Array.from(new Set([...this.deleters.map((contact) => contact.id), ...this.unresolvedDeleterIds]))
          : undefined,
      };
      const group = this.data
        ? await this.contactService.updateContactGroup(this.data.id, request)
        : await this.contactService.createContactGroup(request);
      this.dialogRef.close(group);
    } catch (error) {
      console.error('[CREATE-CONTACT-GROUP] Error creating contact group:', error);
      this.snackBar.open(
        this.appTranslateService.instant('CONTACTS.CREATE_GROUP_SAVE_FAILED'),
        this.appTranslateService.instant('CONTACT_PREFERENCES.OK')
      );
    } finally {
      this.isSaving = false;
    }
  }

  protected confirmDelete(): void {
    if (!this.data || this.isSaving) {
      return;
    }

    const descriptionKey =
      this.data.type === 'shared' ? 'CONTACTS.CONFIRM_DELETE_SHARED_GROUP' : 'CONTACTS.CONFIRM_DELETE_GROUP';

    messageBox(this.storage, this.dialog, {
      data: {
        description: this.appTranslateService.instant(descriptionKey),
        buttons: [
          {
            label: this.appTranslateService.instant('CONTACTS.CREATE_GROUP_CANCEL'),
            action: () => null,
            type: ButtonType.matFlatButton,
          },
          {
            label: this.appTranslateService.instant('CONTACTS.DELETE_GROUP_BTN'),
            action: () => this.handleDelete(),
            type: ButtonType.matRaisedButton,
            color: 'warn',
          },
        ],
      },
    });
  }

  private async handleDelete(): Promise<void> {
    if (!this.data || this.isSaving) {
      return;
    }

    this.isSaving = true;
    try {
      await this.contactService.deleteContactGroup(this.data.id);
      this.dialogRef.close();
    } catch (error) {
      console.error('[CREATE-CONTACT-GROUP] Error deleting contact group:', error);
      this.snackBar.open(
        this.appTranslateService.instant('CONTACTS.DELETE_GROUP_FAILED'),
        this.appTranslateService.instant('CONTACT_PREFERENCES.OK')
      );
    } finally {
      this.isSaving = false;
    }
  }

  private resolveContactsByIds(ids: string[] | undefined, unresolved: string[]): Contact[] {
    const contacts: Contact[] = [];
    for (const id of ids ?? []) {
      const contact = this.contactService.contactById(id);
      if (contact) {
        contacts.push(contact);
      } else {
        unresolved.push(id);
      }
    }
    return contacts;
  }

  private isContactAllowedForGroupType(contact: Contact): boolean {
    return !this.isShared || contact.type === ContactType.Company || contact.type === ContactType.Shared;
  }

  private filterContacts(): void {
    const search = this.search.trim().toLowerCase();
    const eligibleContacts = this.allContacts.filter((contact) => this.isContactAllowedForGroupType(contact));
    const candidates = !search
      ? eligibleContacts
      : eligibleContacts.filter((contact) => {
          return (
            contact.fullName?.toLowerCase().includes(search) ||
            contact.emails?.some((email) => email.value?.toLowerCase().includes(search)) ||
            contact.pbxDepartment?.toLowerCase().includes(search)
          );
        });
    this.filteredContacts.set(candidates);
  }
}
